import React from 'react';
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  InputGroup,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {FaEye, FaEyeSlash} from 'react-icons/fa';
import {getAxiosErrorMessage} from '../libs/helper';
import {isPasswordValid, specialCharsMessage} from './login';

/**
 * ChangePasswordPage
 *
 * Requires an authenticated user (route should be wrapped in RequireAuth).
 * Collects current password, new password, confirm password, validates complexity,
 * calls POST /api/change_password and shows success or error UI.
 */
export default function ChangePasswordPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = React.useState<string>('');
  const [newPassword, setNewPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [showCurrentPassword, setShowCurrentPassword] =
    React.useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = React.useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [success, setSuccess] = React.useState<boolean>(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(undefined);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError(
        'New password must be at least 12 characters and include one lowercase letter, one uppercase letter, one number, and one special character',
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/change_password', null, {
        params: {
          current_password: currentPassword, // eslint-disable-line @typescript-eslint/naming-convention
          new_password: newPassword, // eslint-disable-line @typescript-eslint/naming-convention
        },
        headers: {'X-SKIP-AUTH-LOGOUT': '1'},
      });
      setSuccess(true);
    } catch (error: any) {
      const message =
        getAxiosErrorMessage(error) ?? 'Failed to change password';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className='my-5'>
      <h1>Change Password</h1>

      {error && <Alert variant='danger'>{error}</Alert>}

      {success ? (
        <Alert variant='success'>
          Password changed successfully.
          <div className='mt-3'>
            <Button
              variant='outline-primary'
              onClick={() => {
                void navigate('/');
              }}
            >
              Go to home now.
            </Button>
          </div>
        </Alert>
      ) : (
        <Form
          onSubmit={event => {
            void handleSubmit(event);
          }}
        >
          <Form.Group controlId='current-password' className='mb-3'>
            <Form.Label>Current password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={event => {
                  setCurrentPassword(event.target.value);
                }}
                placeholder='Current password'
                autoComplete='current-password'
                required
              />
              <OverlayTrigger
                placement='right'
                delay={{show: 250, hide: 400}}
                overlay={
                  <Tooltip>{showCurrentPassword ? 'Hide' : 'Show'}</Tooltip>
                }
              >
                <Button
                  onClick={() => {
                    setShowCurrentPassword(!showCurrentPassword);
                  }}
                  variant='outline-secondary'
                >
                  {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <Form.Group controlId='new-password' className='mb-3'>
            <Form.Label>New password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={event => {
                  setNewPassword(event.target.value);
                }}
                placeholder='New password'
                autoComplete='new-password'
                required
              />
              <OverlayTrigger
                placement='right'
                delay={{show: 250, hide: 400}}
                overlay={<Tooltip>{showNewPassword ? 'Hide' : 'Show'}</Tooltip>}
              >
                <Button
                  onClick={() => {
                    setShowNewPassword(!showNewPassword);
                  }}
                  variant='outline-secondary'
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <Form.Group controlId='confirm-password' className='mb-3'>
            <Form.Label>Confirm new password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={event => {
                  setConfirmPassword(event.target.value);
                }}
                placeholder='Confirm new password'
                autoComplete='new-password'
                required
              />
              <OverlayTrigger
                placement='right'
                delay={{show: 250, hide: 400}}
                overlay={
                  <Tooltip>{showConfirmPassword ? 'Hide' : 'Show'}</Tooltip>
                }
              >
                <Button
                  onClick={() => {
                    setShowConfirmPassword(!showConfirmPassword);
                  }}
                  variant='outline-secondary'
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <br />
          <Alert variant='info'>{specialCharsMessage}</Alert>

          <Button type='submit' disabled={loading}>
            {loading ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Changing...
              </>
            ) : (
              'Change password'
            )}
          </Button>
        </Form>
      )}
    </Container>
  );
}
